import React, { useEffect, useRef } from "react";
import { getCanvasIframe, getIframeDocument } from "./canvas-frame";
import { selectionGeometryStore } from "./selection-geometry-store";

/** How often the overlay checks whether the canvas iframe element or its document has been replaced. */
const IFRAME_WATCH_MS: number = 500;

export interface ICanvasFrameEventHandlers {
  /** A keydown in the designer document or in the canvas document. */
  onKeyDown: (event: KeyboardEvent) => void;
  /** A pointerdown inside the canvas document (the designer's own outside-click detection never sees it). */
  onCanvasPointerDown: () => void;
}

interface IAttachedFrame {
  iframe: HTMLIFrameElement;
  doc: Document;
  win: Window;
  detach: () => void;
}

/**
 * Keeps the overlay wired to the canvas iframe across reloads: keyboard + pointer listeners on its
 * document, and a store reset when its document unloads.
 *
 * The canvas document is replaced on screen switch, device switch and preview, and the agent restarts its
 * geometry `seq` with each new document. `pagehide` on the OLD window fires before the new agent can send
 * anything, so resetting there (rather than on the new document's `load`, which can arrive after its
 * first geometry messages) drops nothing valid.
 */
export function useCanvasFrameEvents(handlers: ICanvasFrameEventHandlers): void {
  const handlersRef: React.MutableRefObject<ICanvasFrameEventHandlers> = useRef(handlers);
  handlersRef.current = handlers;

  useEffect((): (() => void) => {
    const onKeyDown = (event: KeyboardEvent): void => handlersRef.current.onKeyDown(event);
    const onPointerDown = (): void => handlersRef.current.onCanvasPointerDown();
    const onPageHide = (): void => selectionGeometryStore.reset();

    let attached: IAttachedFrame | null = null;

    const attach = (): void => {
      const iframe: HTMLIFrameElement | null = getCanvasIframe();
      const doc: Document | null = getIframeDocument(iframe);
      const win: Window | null = iframe?.contentWindow ?? null;
      if (attached && attached.iframe === iframe && attached.doc === doc && attached.win === win) {
        return;
      }
      attached?.detach();
      attached = null;
      if (!iframe || !doc || !win) {
        return;
      }
      doc.addEventListener("keydown", onKeyDown);
      doc.addEventListener("pointerdown", onPointerDown, true);
      win.addEventListener("pagehide", onPageHide);
      iframe.addEventListener("load", attach);
      attached = {
        iframe,
        doc,
        win,
        detach: (): void => {
          doc.removeEventListener("keydown", onKeyDown);
          doc.removeEventListener("pointerdown", onPointerDown, true);
          win.removeEventListener("pagehide", onPageHide);
          iframe.removeEventListener("load", attach);
        }
      };
    };

    document.addEventListener("keydown", onKeyDown);
    attach();
    const watch: number = window.setInterval(attach, IFRAME_WATCH_MS);

    return (): void => {
      document.removeEventListener("keydown", onKeyDown);
      window.clearInterval(watch);
      attached?.detach();
    };
  }, []);
}
